// SearchList.js
import React, { useState, useMemo, useEffect } from "react";
import "swiper/css";
import "swiper/css/free-mode";
import { Filter } from "../../assets/icons";
import "../../css/List/tabList.css";
import SearchCard from "../Search/searchCard";
import Loading from "../Common-components/loading"; // ✅ 기존 로딩 컴포넌트 재사용
import SearchGroup from "../Home/searchGroup";
import { ReactComponent as ArrowIcon } from '../../assets/icons/svg/arrowUnder.svg'

export default function SearchList({ searchResults, searchGroupRef }) {
  const sortOptions = [
    { label: "빠른일자순", value: "fastest" },
    { label: "느린일자순", value: "slowest" },
    { label: "높은가격순", value: "highest" },
    { label: "낮은가격순", value: "lowest" },
  ];
  const [selectedSort, setSelectedSort] = useState(sortOptions[0].value);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1327);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= 1327);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 스크롤 할 쿠키 index 찾기
  useEffect(() => {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
    };

    const scrollBackId = getCookie('scrollBackId');

    // Only proceed if searchResults are available and a scroll cookie exists.
    if (scrollBackId && Array.isArray(searchResults) && searchResults.length > 0) {
      const element = document.getElementById(scrollBackId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // 스크롤 이후 쿠키 삭제
        document.cookie = 'scrollBackId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }
  }, [searchResults]); // Dependency on searchResults

  // ---------- helpers ----------
  const parseDate = (s) => {
    if (!s) return null;
    const day = String(s).split("T")[0];
    const ts = Date.parse(day);
    return Number.isNaN(ts) ? null : ts;
  };

  const toNumber = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return v;
    const n = Number(String(v).replace(/,/g, ""));
    return Number.isNaN(n) ? 0 : n;
  };

  // value를 정렬해 "디폴트 선택" 한 건을 반환
  const pickDefaultValueItem = (item) => {
    const arr = Array.isArray(item?.value) ? [...item.value] : [];
    if (!arr.length) return null;

    // 1) 업데이트 내림차순
    arr.sort((a, b) => {
      const ta = parseDate(a.updateDate) ?? -Infinity;
      const tb = parseDate(b.updateDate) ?? -Infinity;
      return tb - ta;
    });
    /**
     * //  2) 상위 7개
     * const top7 = arr.slice(0, 7);
     * // 3) 출발일 오름차순
     * top7.sort((a, b) => {
     * const da = parseDate(a.depDate) ?? Infinity; 
     * const db = parseDate(b.depDate) ?? Infinity;
     * return da - db;
     * return top7[0] ?? null;
     * });
     */

    // 2)  Top7에서 제한 해제 수정
    const sortedByDep = [...arr].sort((a, b) => {
      const da = parseDate(a.depDate) ?? Infinity;
      const db = parseDate(b.depDate) ?? Infinity;
      if (da !== db) return da - db;
      const ua = parseDate(a.updateDate) ?? -Infinity;
      const ub = parseDate(b.updateDate) ?? -Infinity;
      return ub - ua;
    });
    // console.log(sortedByDep);
    return sortedByDep[0] ?? null;
  };

  // 디폴트 선택건의 4개 가격 중 min/max 계산
  const getPriceExtremes = (valueItem) => {
    if (!valueItem) return { hasPrice: false, min: Infinity, max: -Infinity };

    const candidates = [
      toNumber(valueItem.cruiseinside),
      toNumber(valueItem.cruiseoutside),
      toNumber(valueItem.cruisebalcony),
      toNumber(valueItem.cruisesuite),
      toNumber(valueItem.leadprice),
    ].filter((n) => n > 0); // 0은 노출 안 하니 제외

    if (!candidates.length) return { hasPrice: false, min: Infinity, max: -Infinity };
    return {
      hasPrice: true,
      min: Math.min(...candidates),
      max: Math.max(...candidates),
    };
  };

  // 선택된 정렬에 따라 정렬된 리스트
  const sortedResults = useMemo(() => {
    const list = Array.isArray(searchResults) ? [...searchResults] : [];

    return list
      .map((item) => {
        const def = pickDefaultValueItem(item);
        const depTs = parseDate(def?.depDate) ?? null;
        const { hasPrice, min, max } = getPriceExtremes(def);
        return { item, def, depTs, hasPrice, min, max };
      })
      .sort((A, B) => {
        switch (selectedSort) {
          case "fastest": {
            // 빠른일자순: 가까운 출발일(디폴트 선택건의 depDate)이 빠른 순
            const a = A.depTs ?? Infinity;
            const b = B.depTs ?? Infinity;
            return a - b;
          }
          case "slowest": {
            // 느린일자순: 가장 느린 순
            const a = A.depTs ?? -Infinity;
            const b = B.depTs ?? -Infinity;
            return b - a;
          }
          case "highest": {
            // 높은가격순: 디폴트 선택건의 4개 가격 중 최댓값 내림차순
            // 가격 없는 건 맨 뒤
            if (A.hasPrice && B.hasPrice) return B.max - A.max;
            if (A.hasPrice) return -1;
            if (B.hasPrice) return 1;
            return 0;
          }
          case "lowest": {
            // 낮은가격순: 디폴트 선택건의 4개 가격 중 최솟값 오름차순
            // 가격 없는 건 맨 뒤
            if (A.hasPrice && B.hasPrice) return A.min - B.min;
            if (A.hasPrice) return -1;
            if (B.hasPrice) return 1;
            return 0;
          }
          default:
            return 0;
        }
      })
      .map((x) => x.item);
  }, [searchResults, selectedSort]);
  // --------------------------------

  // 정렬이 바뀌어도 카드가 재생성되지 않도록 안정적인 id 생성
  const buildCardId = (item, fallbackIndex) => {
    if (!item || typeof item !== 'object') return `search-card-${fallbackIndex}`;

    const directId = item.idx || item.id || item.codetocruiseid;
    if (directId) return `search-card-${directId}`;

    if (Array.isArray(item.value)) {
      const nestedWithIdx = item.value.find((v) => v?.idx || v?.codetocruiseid);
      if (nestedWithIdx?.idx) return `search-card-${nestedWithIdx.idx}`;
      if (nestedWithIdx?.codetocruiseid) return `search-card-${nestedWithIdx.codetocruiseid}`;
    }

    return `search-card-${fallbackIndex}`;
  };

  return (
    <>
      <section className="list-section search">
        <div className="filter-sort-container">
          <div className="filter-block">
            {/* <div className="filter-btn">
              <Filter className="filter-icon" /> 필터
            </div>
            <div className="filter-selected">적용된 필터 없음</div> */}
            <SearchGroup ref={searchGroupRef} moTitle='다시 검색하기' forceMobile={true} moIconType='reset' />
          </div>
          <div className="sort-block">
              {isTablet ? (
                <div className="sort-dropdown">
                  <button
                    className="sort-dropdown-toggle"
                    onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  >
                    {sortOptions.find((option) => option.value === selectedSort)?.label}
                    <ArrowIcon />
                  </button>
                  {isSortDropdownOpen && (
                    <ul className="sort-dropdown-menu">
                      {sortOptions.map((option) => (
                        <li
                          key={option.value}
                          className={`sort-dropdown-item ${selectedSort === option.value ? "selected" : ""}`}
                          onClick={() => {
                            setSelectedSort(option.value);
                            setIsSortDropdownOpen(false);
                          }}
                        >
                          {option.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                sortOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`sort-option ${selectedSort === option.value ? "selected" : ""}`}
                    onClick={() => setSelectedSort(option.value)}
                  >
                    {option.label}
                  </div>
                ))
              )}
            </div>
        </div>

        {/* 실제 데이터 렌더링 */}
        <div className="search-result">
          {Array.isArray(sortedResults) && sortedResults.length > 0 ? (
            sortedResults.map((item, index) => {
              const cardId = buildCardId(item, index);
              return (
                <SearchCard
                  {...item}
                  key={cardId}
                  index={index}
                  cardId={cardId}
                />
              );
            })
          ) : (
            <div>검색 결과가 없습니다.</div>
          )}
        </div>
      </section>
    </>
  );
}
